import {Component, Injector, OnInit, OnDestroy, Input, ChangeDetectorRef, HostListener} from '@angular/core';
import {BaseComponent} from "../../base/base.component";
import {UserService} from "../../../services/user/user.service";
import {usersGroup} from "../../../resources/usersGroup";
import {CategoryService} from "../../../services/category/category.service";
import {Company} from "../../../models/company";
import {CompanyService} from "../../../services/company/company.service";
import {Service} from "../../../models/service";
import {SeasonService} from "../../../services/season/season.service";
import {Subscription} from "rxjs";

/** Single nav link (no dropdown). */
export interface NavItemChild {
  path: string[];
  label: string;
}

/**
 * Config for one navbar entry. Visibility can be controlled by
 * agentServiceKey, companyServiceName, hideWhenPaymentRequired, requireUserShop.
 */
export interface NavItemConfig {
  path: string[];
  label: string;
  menuIndex: number;
  /** For agents: show only when JWT companyServices has this key (e.g. "1"). */
  agentServiceKey?: string;
  /** For company manager: show only when company has this service (e.g. "OnlineShop"). */
  companyServiceName?: string;
  /**
   * Show only when JWT companyServices contains this key (e.g. "onlineshop", "subscriptionPortal").
   * Use when JWT has keys like: "onlineshop": "Shop Online", "subscriptionPortal": "Accesso al portale iscrizioni".
   */
  requiredServiceKey?: string;
  /** Hide when company status is paymentFailed or pendingPayment. */
  hideWhenPaymentRequired?: boolean;
  /** For user: show only when checkActiveShop() is true. */
  requireUserShop?: boolean;
  /** Desktop dropdown children; mobile uses main path. */
  children?: NavItemChild[];
  /** Optional query params for the main link. */
  queryParams?: Record<string, string>;
  /** When true, route is built as [...path, getCurrentUser()] (e.g. agent Informazioni). */
  pathHasUserId?: boolean;
}

/**
 * Nav items gated by JWT companyServices key. Key = key from JWT (e.g. "onlineshop", "subscriptionPortal").
 * To add a new service: add one entry here; if the key exists in JWT companyServices, the item appears automatically.
 * Keys are matched case-insensitively.
 */
export const NAV_ITEMS_BY_SERVICE_KEY: Record<string, NavItemConfig> = {
  subscriptionPortal: {
    path: ['/companies/subscriptions'],
    label: 'Iscrizioni',
    menuIndex: 5,
    hideWhenPaymentRequired: true,
    requiredServiceKey: 'dashboard',
  },
  onlineshop: {
    path: ['/companies/shop'],
    label: 'Shop',
    menuIndex: 7,
    hideWhenPaymentRequired: true,
    companyServiceName: 'onlineshop',
    children: [
      { path: ['/companies/shop/orders'], label: 'Ordini' },
      { path: ['/companies/shop'], label: 'Prodotti' },
      { path: ['/companies/shop/categories'], label: 'Categorie' },
      { path: ['/companies/shop/sizes'], label: 'Taglie' },
      { path: ['/companies/shop/colors'], label: 'Colori' },
      { path: ['/shop'], label: 'Il tuo shop' },
    ],
  },
  // Add more services here as you add them to the JWT, e.g.:
  // bookingcamp: { path: ['/companies/booking-camp'], label: 'Booking Camp', menuIndex: 10, hideWhenPaymentRequired: true },
  // pos: { path: ['/companies/pos'], label: 'Pos', menuIndex: 11, hideWhenPaymentRequired: true },
};

/** Nav config per user group. Add new tabs here to show them in the navbar. */
export const NAV_ITEMS: Record<string, NavItemConfig[]> = {
  [usersGroup.admin]: [
    { path: ['/admins/dashboard'], label: 'Dashboard', menuIndex: 0 },
    { path: ['/admins/companies'], label: 'Società', menuIndex: 1 },
    { path: ['/admins/agents'], label: 'Agenti', menuIndex: 2 },
    { path: ['/admins/invoices'], label: 'Report e Fatture', menuIndex: 3 },
    { path: ['/admins/allUsers'], label: 'Utenti', menuIndex: 7 },
    { path: ['/admins/orders'], label: 'Ordini', menuIndex: 8 },
    { path: ['/admins/subscriptions'], label: 'Impostazioni piattaforma', menuIndex: 9 },
  ],
  [usersGroup.companyManager]: [
    { path: ['/companies/dashboard'], label: 'Dashboard', menuIndex: 0, hideWhenPaymentRequired: true,requiredServiceKey: "dashboard"  },
    { path: ['/companies/invoices'], label: 'Report e Fatture', menuIndex: 1, hideWhenPaymentRequired: true, requiredServiceKey: "dashboard"  },
    { path: ['/companies/categories'], label: 'Categorie e Stagioni', menuIndex: 3, hideWhenPaymentRequired: true, requiredServiceKey: "dashboard"  },
    { path: ['/companies/messages'], label: 'Comunicazioni', menuIndex: 4, hideWhenPaymentRequired: true, requiredServiceKey: "dashboard"  },
    { path: ['/companies/athletes'], label: 'Atleti', menuIndex: 6, hideWhenPaymentRequired: true, requiredServiceKey: "dashboard"  },
    
    // Service-gated items (Shop, Iscrizioni, etc.) come from NAV_ITEMS_BY_SERVICE_KEY based on JWT companyServices keys
  ],
  [usersGroup.user]: [
    { path: ['/users/dashboard'], label: 'Dashboard', menuIndex: 0 },
    { path: ['/shop'], label: 'Shop', menuIndex: 1, companyServiceName: 'onlineshop' },
    { path: ['/users/orders'], label: 'Ordini', menuIndex: 2, companyServiceName: 'onlineshop' },
  ],
  [usersGroup.agent]: [
    { path: ['/agents/dashboard'], label: 'Dashboard', menuIndex: 0, agentServiceKey: '1' },
    { path: ['/agents/details'], label: 'Informazioni', menuIndex: 1, pathHasUserId: true }, // routerLink built as ['/agents/details', getCurrentUser()]
    { path: ['/agents/companies'], label: 'Società', menuIndex: 3 },
  ],
  [usersGroup.mister]: [
    { path: ['/misters/dashboard'], label: 'Dashboard', menuIndex: 0 },
    // { path: ['/misters/details'], label: 'Informazioni', menuIndex: 1, pathHasUserId: true }, // routerLink built as ['/misters/details', getCurrentUser()]
    // { path: ['/misters/companies'], label: 'Società', menuIndex: 3 },
  ],
};

@Component({
  selector: 'ui-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent extends BaseComponent implements OnInit, OnDestroy {

  @Input() company: Company = new Company();

  typeOfUser: string = '';
  showPopup: boolean = false;
  showSubscription: boolean = false;
  showSubscriptionShop: boolean = false;
  /** Which menu index has its dropdown open (desktop hover). Use instead of showSubscription/showSubscriptionShop when rendering from config. */
  openDropdownMenuIndex: number | null = null;
  activeMenu: number = 0;
  mobileMenuOpen: boolean = false;

  companyId: string = localStorage.getItem('wisper_companyId') ?? '';
  companyServices?: Service[]

  /** For agents: companyServices from JWT (utils) - no API call, populated immediately */
  agentCompanyServicesFromJwt: Record<string, string> | null = null;

  userName: string = '';
  email: string = '';

  get userInitials(): string {
    const names = this.userName.trim().split(/\s+/).filter(Boolean);
    if (names.length > 1) return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    return (names[0]?.slice(0, 2) || this.email.slice(0, 1) || 'U').toUpperCase();
  }

  currentCompanyId: string = '';

  /** Company status from JWT (e.g. active, paymentFailed, pendingPayment). Used to hide nav items when payment required. */
  companyStatus: string | null = null;

  /** Position for the dropdown panel (fixed, below trigger). */
  dropdownPosition: { top: number; left: number } | null = null;

  private jwtRefreshedSub?: Subscription;

  /** Logo to show: company logo when on shop route and company has one, else WellDone logo. */
  get navbarLogoUrl(): string {
    const url = this.navigate?.url ?? '';
    const isOnShopRoute = url.includes('/shop');
    if (isOnShopRoute && this.company?.logoUrl) {
      return this.company.logoUrl;
    }
    return 'assets/logo-icon.svg';
  }

  constructor(public userService: UserService, public categoryService: CategoryService, public serviceInjector: Injector,
              private companyService: CompanyService, public seasonService: SeasonService, private cdr: ChangeDetectorRef) {
    super(serviceInjector);
    // Populate from JWT immediately so navbar is correct before any async (login stores token first)
    this.typeOfUser = (this.utils.getJwtUserGroup() || localStorage.getItem('wisper_userGroup')) ?? '';
    this.agentCompanyServicesFromJwt = this.utils.getJwtCompanyServices();
    this.companyId = this.utils.getJwtCompanyId();
    this.currentCompanyId = this.companyId;
    this.companyStatus = this.utils.getJwtCompanyStatus();
    console.log('companyId', this.companyId)

    // if (this.typeOfUser === usersGroup.agent.toLowerCase() || this.typeOfUser === usersGroup.companyManager.toLowerCase()) {
    // }
  }


  async ngOnInit() {
    console.log('Well Done WebApp - UI Version 4.3 Loaded');
    this.userName = `${localStorage.getItem('wisper_firstname') ?? ''} ${localStorage.getItem('wisper_lastname') ?? ''}`;
    this.email = localStorage.getItem('wisper_email') ?? '';
    this.currentCompanyId = this.companyId;

    console.log('this.getUserType()', this.getUserType())

    this.jwtRefreshedSub = this.userService.jwtRefreshed$.subscribe(() => this.refreshJwtState());
  }

  /** Re-read JWT-derived state (e.g. after refresh token) and trigger view update. */
  private refreshJwtState(): void {
    this.typeOfUser = (this.utils.getJwtUserGroup() || localStorage.getItem('wisper_userGroup')) ?? '';
    this.agentCompanyServicesFromJwt = this.utils.getJwtCompanyServices();
    this.companyId = this.utils.getJwtCompanyId();
    this.currentCompanyId = this.companyId;
    this.companyStatus = this.utils.getJwtCompanyStatus();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.jwtRefreshedSub?.unsubscribe();
  }

  /**
   * For agents: true if the JWT companyServices includes the given key (e.g. "1" = Dashboard).
   * For non-agents always returns true so nav items are not hidden.
   */
  agentHasService(serviceKey: string): boolean {
    if (this.typeOfUser !== usersGroup.agent) return true;
    return !!this.agentCompanyServicesFromJwt?.[serviceKey];
  }

  companyHasThisService(serviceName: string): boolean {
    if (!this.agentCompanyServicesFromJwt) return false;
    return Object.keys(this.agentCompanyServicesFromJwt).includes(serviceName);
  }

  /**
   * True when JWT companyServices has the given key (e.g. "onlineshop", "subscriptionPortal").
   * Keys are matched case-insensitively so "onlineshop" and "OnlineShop" both work.
   */
  canAccessThisService(serviceKey: string): boolean {
    if (!this.agentCompanyServicesFromJwt || !serviceKey) return false;
    const keyLower = serviceKey.toLowerCase();
    return Object.keys(this.agentCompanyServicesFromJwt).some((k) => k.toLowerCase() === keyLower);
  }

  /** True when company status is paymentFailed or pendingPayment (nav items with hideWhenPaymentRequired are hidden). */
  get isPaymentRequired(): boolean {
    return this.companyStatus === 'paymentFailed' || this.companyStatus === 'pendingPayment';
  }

  /** Whether this nav item should be shown based on JWT companyServices and companyStatus. */
  isNavItemVisible(item: NavItemConfig): boolean {
    if (item.agentServiceKey !== undefined && !this.agentHasService(item.agentServiceKey)) return false;
    if (item.companyServiceName !== undefined && !this.companyHasThisService(item.companyServiceName)) return false;
    if (item.requiredServiceKey !== undefined && !this.canAccessThisService(item.requiredServiceKey)) return false;
    if (item.hideWhenPaymentRequired && this.isPaymentRequired) return false;
    if (item.requireUserShop && !this.checkActiveShop()) return false;
    return true;
  }

  /** Resolved nav config for current user: filtered by visibility and merged with service-gated items. */
  get filteredNavItems(): NavItemConfig[] {
    const base = NAV_ITEMS[this.typeOfUser] ?? [];
    let items: NavItemConfig[] = base.filter((item) => this.isNavItemVisible(item));

    // For company manager: add items from NAV_ITEMS_BY_SERVICE_KEY for each key present in JWT companyServices (no per-service check)
    if (this.typeOfUser === usersGroup.companyManager && this.agentCompanyServicesFromJwt) {
      const serviceItems: NavItemConfig[] = [];
      for (const jwtKey of Object.keys(this.agentCompanyServicesFromJwt)) {
        const keyLower = jwtKey.toLowerCase();
        const config = Object.keys(NAV_ITEMS_BY_SERVICE_KEY).find((k) => k.toLowerCase() === keyLower);
        const itemConfig = config != null ? NAV_ITEMS_BY_SERVICE_KEY[config] : null;
        if (itemConfig) {
          const item: NavItemConfig = { ...itemConfig };
          if (this.isNavItemVisible(item)) serviceItems.push(item);
        }
      }
      items = [...items, ...serviceItems].sort((a, b) => a.menuIndex - b.menuIndex);
    }

    return items.map((item) => ({ ...item }));
  }

  /** Route for agent "Informazioni" (path + current user id). */
  getAgentDetailsRoute(): string[] {
    return ['/agents/details', this.getCurrentUser() ?? ''];
  }

  /** Link for an item: either item.path or agent details route. */
  getItemRoute(item: NavItemConfig): string[] {
    if (item.pathHasUserId) return this.getAgentDetailsRoute();
    return item.path;
  }

  /** Navigate to a dropdown child route and close the dropdown. */
  navigateToChild(child: { path: string[] }, item: NavItemConfig) {
    this.navigate.navigate(child.path);
    this.setDropdownOpen(null);
    this.setActiveMenu(item.menuIndex);
    this.closeMobileMenu();
  }

  /** Navigate to a dropdown child route and close the dropdown. Defers close so router can process first. */
  onDropdownLinkClick(item: NavItemConfig) {
    console.log('onDropdownLinkClick', item);
    setTimeout(() => {
      this.setDropdownOpen(null);
      this.setActiveMenu(item.menuIndex);
      this.closeMobileMenu();
    }, 50);
  }

  showProfilePopup() {
    this.showPopup = true;
  }

  hideProfilePopup() {
    this.showPopup = false;
  }

  toggleProfilePopup() {
    this.showPopup = !this.showPopup;
    if (this.showPopup) {
      this.setDropdownOpen(null);
      this.closeMobileMenu();
    }
  }

  @HostListener('document:click', ['$event'])
  onProfileOutsideClick(event: MouseEvent) {
    if (this.showPopup && !(event.target as HTMLElement | null)?.closest('[data-profile-menu]')) {
      this.hideProfilePopup();
    }
  }

  @HostListener('document:keydown.escape')
  onProfileEscape() {
    this.hideProfilePopup();
  }


  logout(event: any) {
    event.preventDefault();
    this.userService.logout();
  }

  protected readonly usersGroup = usersGroup;

  showSubscriptionsHover() {
    this.showSubscription = true;
    this.openDropdownMenuIndex = 5;
  }

  hideSubscriptionsHover(menuIndex?: number) {
    this.showSubscription = false;
    this.openDropdownMenuIndex = null;
    if (menuIndex) this.setActiveMenu(menuIndex);
  }

  showSubscriptionsHoverShop() {
    this.showSubscriptionShop = true;
    this.openDropdownMenuIndex = 7;
  }

  hideSubscriptionsHoverShop(menuIndex?: number) {
    this.showSubscriptionShop = false;
    this.openDropdownMenuIndex = null;
    if (menuIndex) this.setActiveMenu(menuIndex);
  }

  /** Set which dropdown is open (desktop). Pass null to close. */
  setDropdownOpen(menuIndex: number | null) {
    this.openDropdownMenuIndex = menuIndex;
    this.showSubscription = menuIndex === 5;
    this.showSubscriptionShop = menuIndex === 7;
    if (menuIndex === null) this.dropdownPosition = null;
  }

  /** Open dropdown on click (toggle). */
  toggleDropdown(item: NavItemConfig, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.openDropdownMenuIndex === item.menuIndex) {
      this.setDropdownOpen(null);
    } else {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      this.dropdownPosition = { top: rect.bottom + 4, left: rect.left };
      this.setDropdownOpen(item.menuIndex);
    }
  }

  /** Close dropdown when clicking the backdrop. */
  closeDropdownBackdrop() {
    this.setDropdownOpen(null);
  }

  /** Get the currently open dropdown item config. */
  get openDropdownItem(): NavItemConfig | null {
    if (this.openDropdownMenuIndex === null) return null;
    return this.filteredNavItems.find((i) => i.menuIndex === this.openDropdownMenuIndex) ?? null;
  }

  setActiveMenu(menuIndex: number) {
    this.activeMenu = menuIndex;
    this.utils.activeMenuSubject.next(menuIndex);
  }

  checkActiveShop(): boolean {
    if (!this.companyServices) return false;
    const active = this.companyServices.some(service => service.id === 'ca80d20c-642a-11ee-a43f-fa163e568a50');
    this.utils.hasShopService.next(active);
    return active;
  }

  checkActivePayments(): boolean {
    if (!this.companyServices) return false;
    return this.companyServices.some(service => service.id === '92c67a05-b8d3-48e9-b24a-9be267430391');
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    if (this.mobileMenuOpen) this.hideProfilePopup();
  }

  closeMobileMenu() {
    this.mobileMenuOpen = false;
  }

  /** True when company has onlineshop (cart link shown in profile dropdown). */
  get isShopEnabled(): boolean {
    return this.canAccessThisService('onlineshop');
  }

  get isDashboardEnabled(): boolean {
    return this.canAccessThisService('dashboard');
  }

  getCartQuantity(): number {
    const products = localStorage.getItem('wisper_products');
    const parsed = products ? JSON.parse(products) : [];
    return parsed.reduce((acc: number, p: { quantity?: number }) => acc + (p.quantity || 0), 0);
  }

}
